import { useState, useEffect } from 'react';
import * as Yup from 'yup';
import { Link, useNavigate } from 'react-router';
import { useFormik } from 'formik';
import { set2FAOtpCodeResponse, twoFactorAuthValidateOtpCode } from '../../../redux/auth/twoFactorAuthSlice';
import { AppDispatch } from '../../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { handleError } from '../../../utils/handleError';
import { setCurrentClient } from '../../../redux/auth/authSlice';
import { FormattedMessage, useIntl } from 'react-intl';
import OtpInput from 'react-otp-input';
import '../../../../_metronic/assets/css/custom-trendcommerce.css';
import { RootState } from '../../../redux/store';

const MAX_TOTAL_ATTEMPTS = 5;

interface FormValues {
    otp: string;
}

const useTwoFactorAuthSchema = () => {
    const intl = useIntl();

    return Yup.object().shape({
        otp: Yup.string()
            .matches(/^[0-9]{6}$/, intl.formatMessage({ id: 'AUTH.2FA.INVALID_FORMAT' }))
            .required(intl.formatMessage({ id: 'AUTH.2FA.REQUIRED' }))
    });
};

export function TwoFactorAuth() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const currentClient = useSelector((state: RootState) => state.auth.currentClient);
    const [loading, setLoading] = useState(false);
    const [timerExpired, setTimerExpired] = useState(false);
    const [failedAttempts, setFailedAttempts] = useState(0);

    const formik = useFormik<FormValues>({
        initialValues: {
            otp: '',
        },
        validationSchema: useTwoFactorAuthSchema(),
        onSubmit: async (values, { setStatus, setSubmitting }) => {
            try {
                setLoading(true);

                const twoFactorAuthValidateCodeRequest = {
                    challengeToken: currentClient?.twoFactorChallengeToken,
                    otp: values.otp
                };

                const response = await dispatch(twoFactorAuthValidateOtpCode(twoFactorAuthValidateCodeRequest));

                if (twoFactorAuthValidateOtpCode.rejected.match(response)) {
                    const errorMessage = (response.payload as string) || 'Verification failed';

                    setStatus(errorMessage);
                    setLoading(false);
                    setSubmitting(false);
                    dispatch(set2FAOtpCodeResponse(null));
                    formik.setFieldValue('otp', '');

                    setFailedAttempts(prev => prev + 1);

                    if (failedAttempts + 1 >= MAX_TOTAL_ATTEMPTS) {
                        sessionStorage.removeItem("oodd");
                        sessionStorage.removeItem("pppfff");
                        navigate('/auth/login');
                        window.location.reload();
                    }
                } else if (twoFactorAuthValidateOtpCode.fulfilled.match(response)) {
                    const payload = response.payload;

                    if (payload && payload.validation) {
                        setFailedAttempts(0);

                        dispatch(set2FAOtpCodeResponse(payload));
                        dispatch(setCurrentClient({
                            isTwoFactorActivated: true,
                            accessToken: payload.accessToken,
                            expiration: payload.expiration,
                            refreshToken: null,
                        }));
                        setSubmitting(true);
                        sessionStorage.removeItem("oodd");
                        sessionStorage.removeItem("pppfff");
                        navigate('/dashboard');
                    } else {
                        const errorMsg = payload?.message || 'Verification failed';
                        setStatus(errorMsg);
                        dispatch(set2FAOtpCodeResponse(null));
                        formik.setFieldValue('otp', '');

                        setFailedAttempts(prev => prev + 1);

                        if (failedAttempts + 1 >= MAX_TOTAL_ATTEMPTS) {
                            dispatch(setCurrentClient(null));
                            sessionStorage.removeItem("oodd");
                            sessionStorage.removeItem("pppfff");
                            navigate('/auth/login');
                            window.location.reload();
                        }
                    }
                    setLoading(false);
                }
            } catch (error) {
                const errorMessage = handleError(error);
                setStatus(errorMessage);
                setSubmitting(false);
                setLoading(false);
                dispatch(setCurrentClient(null));
                dispatch(set2FAOtpCodeResponse(null));
                formik.setFieldValue('otp', '');

                setFailedAttempts(prev => prev + 1);
            }
        },
    });

    useEffect(() => {
        if (!currentClient?.twoFactorChallengeToken) {
            navigate('/auth/login', { replace: true });
            return;
        }

        const timer = setTimeout(() => {
            setTimerExpired(true);
        }, 120000);

        return () => clearTimeout(timer);
    }, [currentClient?.twoFactorChallengeToken, navigate]);

    useEffect(() => {
        if (timerExpired) {
            formik.resetForm();
            setTimerExpired(false);
            setFailedAttempts(0);
            dispatch(setCurrentClient(null));
            navigate('/auth/login');
        }
    }, [timerExpired, navigate, formik, dispatch]);

    return (
        <form
            className='form p-20 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            id='kt_login_two_factor_auth_form'
            onSubmit={formik.handleSubmit}
        >
            <div className='text-center mt-20 mb-10'>
                <h1 className='text-gray-900 fw-bolder mb-3'>
                    <FormattedMessage id='AUTH.2FA.TITLE' />
                </h1>
                <div className='text-gray-500 fw-semibold fs-6 mt-7'>
                    <FormattedMessage id='AUTH.2FA.DESC' />
                </div>
            </div>

            {formik.status && (
                <div className='mb-lg-15 alert alert-danger'>
                    <div className='alert-text font-weight-bold'>
                        {formik.status ? formik.status : <FormattedMessage id='AUTH.2FA.ERROR' />}
                    </div>
                </div>
            )}

            <div className='d-flex flex-wrap justify-content-center pb-lg-0 mt-12 mb-12'>
                <OtpInput
                    value={formik.values.otp}
                    onChange={(otpValue) => {
                        formik.setFieldValue('otp', otpValue);
                        formik.setStatus('');
                    }}
                    numInputs={6}
                    inputType='number'
                    renderInput={(props) => (
                        <input
                            {...props}
                            className="form-control bg-transparent w-40px w-md-60px h-40px h-md-60px fs-5 text-center mx-1 rounded border border-dark border-opacity-30"
                        />
                    )}
                />
                {formik.touched.otp && formik.errors.otp && (
                    <div className='fv-plugins-message-container'>
                        <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.otp}</span>
                        </div>
                    </div>
                )}
            </div>

            <div className='d-flex flex-wrap justify-content-center pb-lg-0'>
                <button
                    type='submit'
                    id='kt_password_reset_submit'
                    className='btn btn-primary me-4'
                    disabled={loading}
                >
                    <span className='indicator-label'>
                        {loading ? <FormattedMessage id='GENERAL.PLEASE_WAIT' /> : <FormattedMessage id='GENERAL.SUBMIT_BUTTON' />}
                    </span>
                    {loading && (
                        <span className='indicator-progress'>
                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                        </span>
                    )}
                </button>
                <Link to='/auth/login'>
                    <button
                        type='button'
                        id='kt_login_password_reset_form_cancel_button'
                        className='btn btn-light'
                        disabled={formik.isSubmitting || !formik.isValid}
                    >
                        <FormattedMessage id='GENERAL.CANCEL_BUTTON' />
                    </button>
                </Link>
            </div>
        </form>
    );
}
